<script lang="ts">
  // BJL-CHECK: RadioGroupBare owns selection, labels, keyboard behavior, and ARIA for each
  // extracted settings panel. The livery image is supplied through its presentation snippet.
  import { RadioGroupBare, type RadioOption } from '@byjackli-labs/ui/RadioGroup'
  import type { OptionRenderContext } from '@byjackli-labs/ui/types'
  import { storeSelect } from '../../lib/store.svelte'
  import { useSettingsStore, type MapThemePref, type VehicleDisplay } from '../../store/settings'
  import { Colors } from '../../ui/colors'
  import { Type } from '../../ui/theme'
  import { currentMapScheme } from './mapTheme'
  import { type LiveryId, type LiveryVehicle } from './liveries'
  import { liveryFlankPreview, liveryModelPreview, LIVERY_PREVIEW_ASPECT } from './liveryPreview'
  import {
    DISPLAY_OPTIONS,
    LIVERY_PICKERS,
    THEME_OPTIONS,
    type LayersChoiceId,
  } from './layersSheetOptions'

  // One exclusive setting's control, whatever surface the sheet puts it on: `display` and
  // `theme` are levels behind a summary row, the two livery grids sit inline on the root
  // level. Where it renders is LayersSheet's decision, not this component's.
  interface Props { choice: LayersChoiceId }
  const { choice }: Props = $props()

  const vehicleDisplay = storeSelect(useSettingsStore, (state) => state.vehicleDisplay)
  const subwayLivery = storeSelect(useSettingsStore, (state) => state.subwayLivery)
  const busLivery = storeSelect(useSettingsStore, (state) => state.busLivery)
  const mapTheme = storeSelect(useSettingsStore, (state) => state.mapTheme)

  const liveryPreviews = $state<Record<string, string>>({})
  $effect(() => {
    if (choice !== 'subway' && choice !== 'bus') return
    let alive = true
    const picker = LIVERY_PICKERS[choice]
    for (const option of picker.options) {
      void liveryModelPreview(option.value as LiveryId, choice).then((url) => {
        if (alive && url) liveryPreviews[`${choice}:${option.value}`] = url
      })
    }
    return () => { alive = false }
  })

  const resolvedScheme = $derived.by(() => {
    mapTheme.value
    return currentMapScheme()
  })
  const followsSomething = $derived(mapTheme.value === 'device' || mapTheme.value === 'schedule')
</script>

{#if choice === 'display'}
  <RadioGroupBare
    class="layers-radio"
    options={DISPLAY_OPTIONS}
    value={vehicleDisplay.value}
    onchange={(event) =>
      useSettingsStore.getState().setVehicleDisplay(event.detail.value as VehicleDisplay)}
  />
{:else if choice === 'theme'}
  <RadioGroupBare
    class="layers-radio"
    options={THEME_OPTIONS}
    value={mapTheme.value}
    onchange={(event) => useSettingsStore.getState().setMapTheme(event.detail.value as MapThemePref)}
  />
  {#if followsSomething}
    <span style="display: block; color: {Colors.textMuted}; {Type.caption}; padding: 2px 0 8px;">
      Showing the {resolvedScheme} map right now. Only the map changes; the app’s own panels keep one palette.
    </span>
  {/if}
{:else}
  {@const vehicle = choice as LiveryVehicle}
  {@const picker = LIVERY_PICKERS[vehicle]}
  {@const chosen = vehicle === 'subway' ? subwayLivery.value : busLivery.value}
  {#snippet liverySwatch(option: RadioOption, context: OptionRenderContext)}
    <span
      class="livery-swatch"
      class:selected={context.selected}
      style="aspect-ratio: {LIVERY_PREVIEW_ASPECT};"
    >
      <img
        src={liveryPreviews[`${vehicle}:${option.value}`] ??
          liveryFlankPreview(option.value as LiveryId, vehicle)}
        alt=""
      />
    </span>
  {/snippet}
  <RadioGroupBare
    class="layers-radio layers-livery"
    variant="card"
    orientation="horizontal"
    options={picker.options}
    value={chosen}
    radio={liverySwatch}
    onchange={(event) =>
      useSettingsStore.getState().setLivery(vehicle, event.detail.value as LiveryId)}
  />
{/if}

<style>
  .livery-swatch {
    display: block;
    width: 100%;
    overflow: hidden;
    border-radius: 5px;
    background-color: #0f1319;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
    opacity: 0.72;
    transition: opacity var(--bjl-transition-base, 0.18s ease);
  }
  .livery-swatch.selected { opacity: 1; }
  .livery-swatch img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
</style>
